import React from 'react'

import { FoldingPanel } from '../folding-panel'
import { FormPanel } from '../form-panel'

/**
 * @typedef {object} BlockConfig
 * @property {boolean} [foldable=true] - Si es `false`, el formulario se renderiza sin el panel plegable.
 * @property {boolean} [isFolded] - Define si el panel debe estar inicialmente contraído (solo aplica si `foldable` es `true`).
 * @property {string} title - El título del panel plegable (solo aplica si `foldable` es `true`).
 */

/**
 * @typedef {object} FormFoldingPanelProps
 * @property {BlockConfig} block - La configuración del bloque que determina si el formulario es plegable y su estado inicial.
 * @property {object} data - Los datos del formulario.
 * @property {object} combos - Los datos de combos/opciones para campos de selección.
 * @property {object} fieldList - La lista de definiciones de campos del formulario.
 * @property {object} [errors] - Los errores de validación del formulario.
 * @property {boolean} [showErrors] - Si es `true`, muestra los mensajes de error en los campos.
 * @property {React.ReactNode} [extraContent] - Contenido adicional a renderizar al final del formulario.
 */

/**
 * Un componente que renderiza un `FormPanel` opcionalmente envuelto en un `FoldingPanel`.
 * 
 * Proporciona una forma conveniente de crear formularios que pueden ser colapsables o estáticos
 * basándose en la configuración del bloque. Si `block.foldable` es `false`, el formulario se
 * renderiza directamente sin el contenedor plegable.
 * 
 * @component
 * @example
 * const blockConfig = {
 *   foldable: true,
 *   isFolded: false,
 *   title: 'Datos Personales',
 *   items: [
 *     [{ ref: 'name', conf: { xs: 12, md: 6 } }],
 *     [{ ref: 'email', conf: { xs: 12, md: 6 } }],
 *   ],
 * };
 * 
 * const fieldList = {
 *   name: { componentType: 'text', fromData: (data) => ({ value: data.name, onChange: handleChange }) },
 *   email: { componentType: 'text', fromData: (data) => ({ value: data.email, onChange: handleChange }) },
 * };
 * 
 * <FormFoldingPanel
 *   block={blockConfig}
 *   data={formData}
 *   combos={combosData}
 *   fieldList={fieldList}
 *   errors={validationErrors}
 *   showErrors={true}
 * />
 * 
 * @param {FormFoldingPanelProps} props - Las props del componente FormFoldingPanel.
 */
export const FormFoldingPanel = (props) => {
  return props.block.foldable !== false ? (
    <FoldingPanel isExpanded={!props.block.isFolded} title={props.block.title}>
      <FormPanel {...props} />
    </FoldingPanel>
  ) : (
    <FormPanel {...props} />
  )
}

export default FormFoldingPanel
